/**
 * Google AdSense Configuration
 * 
 * Maps ad slot names (used in components) to actual Google AdSense ad unit IDs.
 * 
 * SETUP INSTRUCTIONS:
 * 1. Log into Google AdSense console: https://www.google.com/adsense/
 * 2. Navigate to Ads > By ad unit
 * 3. Create new ad units for each placement below
 * 4. Copy the ad unit ID (numeric string like "1234567890")
 * 5. Replace the placeholder values below with your actual ad unit IDs
 * 
 * NOTE: Keep placeholder slot names as keys - they're used throughout the codebase.
 * Only update the values (ad unit IDs) after creating them in AdSense.
 */

export const adsenseConfig = {
  // ============================================
  // STANDARD DISPLAY ADS (728x90 / 468x60 / 300x250)
  // ============================================
  
  // Dashboard
  '72890_dashboard_top': '7242997554', // Dashboard: between AI Perfect Match and Forecast Card
  
  // My Programs Page
  '72890_myprograms_inline_3': '4102539330', // My Programs: after 3rd program card
  '72890_myprograms_inline_8': '7681981254', // My Programs: after 8th program card
  '72890_myprograms_inline_13': '3112180852', // My Programs: after 13th program card (every 5th after 8th)
  '72890_myprograms_inline_18': '5549259049', // My Programs: after 18th program card
  '72890_myprograms_inline_23': '5055817915', // My Programs: after 23rd program card
  // Note: Dynamic slots like `72890_myprograms_inline_${index+1}` will use the pattern above
  '72890_myprograms_featured_fallback': '7331337908', // My Programs: FeaturedSponsor fallback ad
  
  // Planning Page
  '72890_planning_under_gameplan': '2429654576', // Planning: under "Your Summer Game Plan" card
  
  // Blog & Article Pages
  '300250_blog_sidebar': '9486017516', // Blog page: right rail sidebar
  '300250_article_sidebar': '1116572906', // Article pages: right rail sidebar
  '72890_article_top': '8803491231', // Article pages: top banner (above content)
  '72890_article_bottom': '3910967644', // Article pages: bottom banner (before related articles)
  '72890_article_inline_1': '8172935843', // Article pages: first inline ad (after first AD MARKER)
  '72890_article_inline_2': '7490409564', // Article pages: second inline ad (after second AD MARKER)
  '72890_article_inline_3': '1610014038', // Article pages: third inline ad (after third AD MARKER)
  // Note: Dynamic slots like `72890_article_inline_${index}` will use the pattern above
  
  // Landing Page
  '2345678901': '3441970458', // Landing page: existing placement
  
  // ============================================
  // NATIVE ADS (fluid layout)
  // ============================================
  
  // Search Results (SmartSearchEngine)
  '4567890123': '6323284660', // Search Results: position 3 (after 2nd result)
  '4567890123_8': '3551164555', // Search Results: after 8th result
  '4567890123_13': '3505549635', // Search Results: after 13th result (every 5th after 8th)
  '4567890123_18': '7467069277', // Search Results: after 18th result
  '4567890123_23': '9294445820', // Search Results: after 23rd result
  // Note: Dynamic slots like `4567890123_${index+1}` will use the pattern above
  
  // Recommendations (SmartSearchEngine)
  '5678901234': '5876562107', // Recommendations: position 4 (after 3rd recommendation)
  '5678901234_9': '7981364156', // Recommendations: after 9th recommendation
  '5678901234_14': '4563480437', // Recommendations: after 14th recommendation (every 5th after 9th)
  '5678901234_19': '6668282487', // Recommendations: after 19th recommendation
  '5678901234_24': '8566304626', // Recommendations: after 24th recommendation
  // Note: Dynamic slots like `5678901234_${index+1}` will use the pattern above
  
  // Nearby Youth Programs (DiscoveryEnhanced)
  '6789012345': '7792279008', // Nearby Youth Programs: position 3 (after 2nd program)
};

/**
 * Resolves an ad slot name to its corresponding AdSense ad unit ID.
 * 
 * @param {string} slotName - The ad slot name used in components
 * @returns {string} - The AdSense ad unit ID, or the original slotName if not found
 * 
 * @example
 * resolveAdSlot('72890_dashboard_top') // Returns the mapped ID or '72890_dashboard_top' if not found
 */
export function resolveAdSlot(slotName) {
  if (!slotName) return slotName;
  
  // Direct lookup
  if (adsenseConfig[slotName]) {
    return adsenseConfig[slotName];
  }
  
  // Handle dynamic slot names (e.g., "72890_myprograms_inline_13")
  // Try to match patterns for dynamic slots
  const dynamicPatterns = [
    // My Programs inline ads: 72890_myprograms_inline_{number}
    {
      pattern: /^72890_myprograms_inline_(\d+)$/,
      baseKey: '72890_myprograms_inline_',
      getKey: (match) => `72890_myprograms_inline_${match[1]}`
    },
    // Article inline ads: 72890_article_inline_{number}
    {
      pattern: /^72890_article_inline_(\d+)$/,
      baseKey: '72890_article_inline_',
      getKey: (match) => `72890_article_inline_${match[1]}`
    },
    // Search results native ads: 4567890123_{number}
    {
      pattern: /^4567890123_(\d+)$/,
      baseKey: '4567890123_',
      getKey: (match) => `4567890123_${match[1]}`
    },
    // Recommendations native ads: 5678901234_{number}
    {
      pattern: /^5678901234_(\d+)$/,
      baseKey: '5678901234_',
      getKey: (match) => `5678901234_${match[1]}`
    }
  ];
  
  for (const { pattern, getKey } of dynamicPatterns) {
    const match = slotName.match(pattern);
    if (match) {
      const key = getKey(match);
      if (adsenseConfig[key]) {
        return adsenseConfig[key];
      }
    }
  }
  
  // Fallback: return original slot name if no mapping found
  // This allows components to work with placeholder names during development
  return slotName;
}

export default adsenseConfig;

